// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {debounce} from 'lodash';
import CardCarousel from '@homeaway/react-card-carousel';
import {
    AspectRatioCard,
    AspectRatioCardImage,
    AspectRatioCardTitle,
    AspectRatioCardText
} from '@homeaway/react-aspect-ratio-card';

import {HA_SERP_SEARCH_TERM_CAROUSEL} from '../../abacus/experiments';
import routerActions from '../../actions/router';
import Experiment from '../Experiment/Experiment';
import {media} from '../../utils/breakpoints';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';

import type {State as ReduxState} from '../../reducers/index';

import './SearchTermCarousel.less';

type State = {
    cards: Array<Object>,
    index: number
};

type StateProps = {
    isXsScreenWidth: boolean,
    isBoundingBoxSearch?: boolean
};

type Props = {
    aspectRatio: string,
    results?: any,
    locationSearch: typeof routerActions.search
} & StateProps;

export class SearchTermCarousel extends Component<Props, State> {
    static defaultProps = {
        aspectRatio: '4x3'
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            index: 0,
            cards: this.renderCards()
        };
    }

    shouldShowCarousel = () => {
        const {searchTerm = {}, hitCollection = {}} = this.props.results || {};

        return (
            !this.props.isBoundingBoxSearch &&
            searchTerm &&
            searchTerm.description === 'Florida, USA' &&
            hitCollection &&
            hitCollection.total
        );
    };

    // handle onScroll event
    handleScroll = () => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    };

    // Centers the controls within the image using the method from AspectRatioCard
    getControlOffset = (aspectRatio: string) => {
        return (width: number, controlHeight: string) => {
            return AspectRatioCard.getControlOffset(
                width,
                controlHeight,
                aspectRatio
            );
        };
    };

    handlePrevNext = (
        evt: SyntheticEvent<HTMLInputElement>,
        newIndex: number
    ) => {
        this.setState({index: newIndex});
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    };

    handleOnClick = (evt: SyntheticEvent<HTMLInputElement>) => {
        let q = evt.currentTarget.getAttribute('value');
        if (q) {
            this.props.locationSearch({q});
        }

        const options = {delayProcessing: true};
        edapTrigger(
            GENERIC_EVENT,
            {
                eventaction: 'searchTerm carousel',
                eventlabel: 'click'
            },
            options
        );
    };

    // Utility method to generate Florida, USA subregion card content for the carousel.
    renderCards = () => {
        let cards = [];

        // hardcoded florida destinations
        let destinations = [
            {
                title: 'Destin',
                meta: '10,288  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/d87e4c30-b5f1-4a30-bb12-7883a29cabbc.carousel-m.jpg',
                searchTerm: 'Destin, FL, USA'
            },
            {
                title: 'Orlando',
                meta: '33,932  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/03dc5f84-db9b-4fb1-a952-a250e9b69344.carousel-m.jpg',
                searchTerm: 'Orlando, FL, USA'
            },
            {
                title: 'Panama City Beach',
                meta: '11,832  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/152e6ed0-faa8-4a48-b36a-ed0f527b68ec.carousel-m.jpg',
                searchTerm: 'Panama City Beach, FL, USA'
            },
            {
                title: 'Key West',
                meta: '1,622  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/ee96f6ac-b184-49e3-a9d5-42c1a54e57e1.carousel-m.jpg',
                searchTerm: 'Key West, FL, USA'
            },
            {
                title: 'Fort Lauderdale',
                meta: '4,667  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/a075a2e4-3ba1-4177-8363-ffc2c0dd9ff4.carousel-m.jpg',
                searchTerm: 'Fort Lauderdale, FL, USA'
            },
            {
                title: 'Pensacola Beach',
                meta: '2,730  properties',
                imgUri:
                    'https://odis.homeaway.com/odis/destination/08c33a38-a64c-40d6-9d85-d8c8f72ddd0c.carousel-m.jpg',
                searchTerm: 'Pensacola Beach, FL, USA'
            }
        ];

        for (let i = 0; i < destinations.length; i++) {
            cards.push(
                <a
                    className="SearchTermCarousel__link"
                    onClick={this.handleOnClick}
                    value={destinations[i].searchTerm}
                >
                    <AspectRatioCard
                        aspectRatio={this.props.aspectRatio}
                        key={`card-${i + 1}`}
                    >
                        <AspectRatioCardImage
                            data-slot={'figure'}
                            imageUri={destinations[i].imgUri}
                        />
                        <AspectRatioCardTitle
                            data-slot={'caption'}
                            title={destinations[i].title}
                        />
                        <AspectRatioCardText
                            data-slot={'content'}
                            text={destinations[i].meta}
                        />
                    </AspectRatioCard>
                </a>
            );
        }
        return cards;
    };

    render() {
        return (
            this.shouldShowCarousel() && (
                <Experiment
                    name={HA_SERP_SEARCH_TERM_CAROUSEL}
                    variant={Experiment.ON}
                >
                    <div onScroll={debounce(this.handleScroll, 200)}>
                        <CardCarousel
                            breakpoints={{
                                '1200': 4,
                                '992': 3,
                                '768': 4,
                                '480': 3,
                                '0': 3
                            }}
                            cardIndex={this.state.index}
                            getControlOffset={this.getControlOffset(
                                this.props.aspectRatio
                            )}
                            onNextClick={this.handlePrevNext}
                            onPreviousClick={this.handlePrevNext}
                            nextLabel={'Next'}
                            previousLabel={'Previous'}
                            title={'Popular places to stay in Florida'}
                            scrolling={this.props.isXsScreenWidth}
                        >
                            {this.state.cards}
                        </CardCarousel>
                    </div>
                </Experiment>
            )
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => {
    let {viewport, searchParams} = state;
    const breakpoint = viewport.breakpoint;
    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });
    const isBoundingBoxSearch = searchParams.isBoundingBoxSearch;

    return {
        isXsScreenWidth,
        isBoundingBoxSearch
    };
};

const mapDispatchToProps = {
    locationSearch: routerActions.locationSearch
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(SearchTermCarousel);
